import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Stat } from "@/components/shared";
import { PlanGrid } from "@/components/Plans";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

const Invoices = ({ items }) => (
  <Panel className="mb-8" testid="billing-history">
    <h2 className="mb-3 text-lg font-semibold">Billing history</h2>
    {items.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="billing-history-empty">No invoices yet.</p> : items.map((i) => (
      <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 border-b py-2 text-sm last:border-0" data-testid={`invoice-${i.id}`}>
        <span>{fmtDate(i.created)} · {i.number || i.id}</span>
        <span className="capitalize">{i.status} · ${(i.status === "paid" ? i.amount_paid : i.amount_due).toFixed(2)}</span>
        {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary underline" data-testid={`invoice-link-${i.id}`}>View</a>}
      </div>
    ))}
  </Panel>
);

export default function Billing() {
  const { user, refresh } = useAuth();
  const [quota, setQuota] = useState(null);
  const [info, setInfo] = useState(null);
  const [invoices, setInvoices] = useState(null);
  useEffect(() => {
    api.get("/ai/quota").then((r) => setQuota(r.data));
    api.get("/payments/subscription").then((r) => setInfo(r.data));
    api.get("/payments/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  }, [user.plan, user.subscription?.status, user.subscription?.cancel_at_period_end]);
  const sub = user.subscription;
  const live = sub && ["active", "trialing", "past_due"].includes(sub.status);
  const act = async (path, msg) => {
    try { await api.post(path); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = () => window.confirm("Cancel your subscription? You keep your plan until the end of the current billing period.") && act("/payments/cancel", "Your subscription will end at the close of this billing period");
  const portal = async () => {
    try { const { data } = await api.post("/payments/portal", { return_url: window.location.origin }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Subscription" title="Plan & billing" />
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat testid="billing-current-plan" labelText="Current plan" value={quota?.plan_name || "—"} />
        <Stat testid="billing-credits" labelText="AI credits this month" value={quota ? `${quota.credits_used}/${quota.credits_limit}` : "—"} />
        <Stat testid="billing-spend" labelText="AI spend cap" value={quota ? `$${quota.spend_used_usd.toFixed(2)} / $${quota.spend_limit_usd}` : "—"} hint="Protects you and us from runaway usage" />
      </div>
      <Panel className="mb-8" testid="billing-subscription-panel">
        {info?.mode === "test" && <p className="mb-2 text-sm text-amber-700" data-testid="billing-test-mode">Test mode: no real charges. Test card 4242 4242 4242 4242.</p>}
        {sub?.status ? (
          <div className="space-y-1 text-sm">
            <p>Status: <b className="capitalize" data-testid="billing-sub-status">{sub.status.replace("_", " ")}</b></p>
            {sub.current_period_end && sub.status !== "past_due" && <p data-testid="billing-period-end">{sub.cancel_at_period_end ? "Access ends" : "Renews"} on {fmtDate(sub.current_period_end)}</p>}
            {sub.status === "past_due" && <p className="text-red-600" data-testid="billing-past-due">Your last payment failed and Stripe will retry it. Update your payment method to keep your plan — access is at risk.</p>}
          </div>
        ) : <p className="text-sm text-muted-foreground">You don't have a paid subscription.</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {info?.has_customer && <Button size="sm" onClick={portal} data-testid="billing-portal-button">Manage billing & payment method</Button>}
          {live && !sub.cancel_at_period_end && <Button variant="outline" size="sm" onClick={cancel} data-testid="billing-cancel-button">Cancel subscription</Button>}
          {live && sub.cancel_at_period_end && <Button variant="outline" size="sm" onClick={() => act("/payments/resume", "Subscription resumed")} data-testid="billing-resume-button">Keep my subscription</Button>}
        </div>
      </Panel>
      {invoices === null ? <Panel className="mb-8" testid="billing-history-loading"><p className="text-sm text-muted-foreground">Loading billing history…</p></Panel> : <Invoices items={invoices} />}
      <PlanGrid audience={user.role === "recruiter" ? "recruiter" : "candidate"} />
    </div>
  );
}
